import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';

import { ThemeProvider } from './contexts/ThemeContext.js';
import { AuthGuard } from './components/AuthGuard.js';
import { LandingPage } from './pages/Landing.js';
import { LoginPage } from './pages/Login.js';
import { SignupPage } from './pages/Signup.js';
import { ForgotPasswordPage } from './pages/ForgotPassword.js';
import { OnboardingPage } from './pages/Onboarding.js';
import { DashboardPage } from './pages/Dashboard.js';
import { ProfilePage } from './pages/Profile.js';
import { DenyListPage } from './pages/DenyList.js';
import { MealPlansPage } from './pages/MealPlans.js';
import { MealPlanDetailPage } from './pages/MealPlanDetail.js';
import { ShoppingListPage } from './pages/ShoppingList.js';
import { IngredientChecklistPage } from './pages/IngredientChecklist.js';
import { PreferencesPage } from './pages/Preferences.js';
import { SharedViewPage } from './pages/SharedView.js';
import { AdminPage } from './pages/Admin.js';
import { FavoritesPage } from './pages/Favorites.js';
import { RecipeDetailPage } from './pages/RecipeDetail.js';
import { CookingModePage } from './pages/CookingMode.js';
import { AchievementsPage } from './pages/Achievements.js';
import { SavingsPage } from './pages/Savings.js';
import { RewardsPage } from './pages/Rewards.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403/404
        if ([401, 403, 404].includes(error?.status)) return false;
        return failureCount < 2;
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/shared/:token" element={<SharedViewPage />} />

            {/* Protected routes */}
            <Route
              path="/onboarding"
              element={<AuthGuard><OnboardingPage /></AuthGuard>}
            />
            <Route
              path="/dashboard"
              element={<AuthGuard><DashboardPage /></AuthGuard>}
            />
            <Route
              path="/profile"
              element={<AuthGuard><ProfilePage /></AuthGuard>}
            />
<Route
              path="/deny-list"
              element={<AuthGuard><DenyListPage /></AuthGuard>}
            />
            <Route
              path="/meal-plans"
              element={<AuthGuard><MealPlansPage /></AuthGuard>}
            />
            <Route
              path="/meal-plans/:id"
              element={<AuthGuard><MealPlanDetailPage /></AuthGuard>}
            />
            <Route
              path="/shopping-list/:id"
              element={<AuthGuard><ShoppingListPage /></AuthGuard>}
            />
            <Route
              path="/ingredient-checklist/:mealPlanId"
              element={<AuthGuard><IngredientChecklistPage /></AuthGuard>}
            />
            <Route
              path="/preferences"
              element={<AuthGuard><PreferencesPage /></AuthGuard>}
            />
            <Route path="/settings" element={<Navigate to="/profile" replace />} />
            <Route
              path="/admin"
              element={<AuthGuard><AdminPage /></AuthGuard>}
            />
            <Route
              path="/favorites"
              element={<AuthGuard><FavoritesPage /></AuthGuard>}
            />
            <Route
              path="/recipes/:id"
              element={<AuthGuard><RecipeDetailPage /></AuthGuard>}
            />
            <Route
              path="/recipes/:id/cook/:stepIndex"
              element={<AuthGuard><CookingModePage /></AuthGuard>}
            />
            <Route
              path="/achievements"
              element={<AuthGuard><AchievementsPage /></AuthGuard>}
            />
            <Route
              path="/savings"
              element={<AuthGuard><SavingsPage /></AuthGuard>}
            />
            <Route
              path="/rewards"
              element={<AuthGuard><RewardsPage /></AuthGuard>}
            />
            {/* Fallback — send unknown paths to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);
